import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { cn } from '@/core/lib/utils';
import type { FieldProvenance } from '../types';
import type { TaskFieldChipPart } from '../utils/taskFieldChipValue';

interface TaskFieldChipProps {
  /** Draft field key — drives the testid so specs address one chip. */
  field: string;
  /** Only the AI tab has one: a blank task interprets nothing. */
  provenance?: FieldProvenance;
  /** Testid namespace, so the blank-task chips are addressable apart from a proposal's. */
  testIdPrefix?: string;
  icon: ReactNode;
  label: string;
  /**
   * The values on the chip, each with the colour it carries in its own right. Empty means the
   * model could not determine the field (QTC-3).
   */
  value: TaskFieldChipPart[] | null;
  isNew?: boolean;
  isAmbiguous?: boolean;
  /** Editor for this field, shown in a popover when the chip is clicked. */
  children: ReactNode;
}

/**
 * One task field as the design draws it: a 30px pill, white on its panel, grey icon and
 * label, value in primary. Used by both quick-create tabs — the AI proposal and the blank
 * form set the same fields, so they get the same control.
 *
 * Clicking a chip opens the field's editor — the chips are the edit surface, so nothing
 * is created that the user could not correct first (QTC-6).
 *
 * The AI tab's extra state comes from the proposal contract and is never inferred:
 * `provenance` (carried by `data-provenance`, not a dot — the design draws none) decides
 * whether it reads as AI-suggested, `isAmbiguous` that it needs a choice, `isNew` that
 * confirming adds something to the whole board. The blank form passes none of them.
 */
export function TaskFieldChip({
  field,
  provenance,
  testIdPrefix = 'ai-proposal-field',
  icon,
  label,
  value,
  isNew = false,
  isAmbiguous = false,
  children,
}: TaskFieldChipProps) {
  const { t } = useTranslation('ai');
  const parts = value ?? [];
  const isEmpty = parts.length === 0;
  const spoken = parts.map((part) => part.label).join(', ');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-${field}`}
          data-provenance={provenance}
          aria-label={`${label}: ${isEmpty ? t('provenance.notSupplied') : spoken}`}
          className={cn(
            'flex h-[30px] items-center gap-1.5 rounded-full border bg-card px-3 text-xs',
            'transition-colors hover:border-primary/50 focus-visible:border-primary',
            isAmbiguous && 'border-warning',
            isNew && 'border-primary border-dashed',
          )}
        >
          <span className="text-muted-foreground [&_svg]:size-3.5" aria-hidden>
            {icon}
          </span>
          <span className="text-muted-foreground">{label}</span>
          <span
            data-testid={`${testIdPrefix}-${field}-value`}
            className={cn(
              'flex items-center gap-1.5 font-medium',
              isEmpty ? 'text-muted-foreground/70 italic' : 'text-primary',
            )}
          >
            {isEmpty
              ? t('quickCreate.emptyValue')
              : parts.map((part, index) => (
                  <span
                    key={`${index}:${part.label}`}
                    className="flex items-center gap-1"
                    style={{ color: part.color ?? undefined }}
                  >
                    {/* The same dot the task detail puts before a status name: a board
                        identifies a column and a tag by colour, and the chip named them in
                        plain text. Only the first carries the testid — specs address the
                        field, and a chip with one value is the ordinary case. */}
                    {part.color && (
                      <span
                        data-testid={index === 0 ? `${testIdPrefix}-${field}-dot` : undefined}
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: part.color }}
                        aria-hidden
                      />
                    )}
                    {part.label}
                  </span>
                ))}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        {children}
      </PopoverContent>
    </Popover>
  );
}
